import axios from "axios";
import Cookies from "js-cookie";
import moment from "moment";
import { toast } from "../utils/toast";
import translations from "../components/common/Translations";
import { withApiError, withApiErrorResponse, normalizeApiErrorData, apiErrorMessage } from "../utils/apiError";

const baseURL = process.env.REACT_APP_BASE_BACKEND_API_URL;

const getBackendErrorMessage = (error) =>
  error?.response?.data?.body?.error ||
  error?.response?.data?.body?.value ||
  error?.response?.data?.message ||
  error?.body?.error ||
  error?.body?.value;

export const fetchTasks = async ({
  workflowId,
  currentPage,
  pageSize,
  priority,
  active,
  areaCode,
  accountNo,
  selectedUserId,
  selectedSubactivity,
  fromDate,
  toDate,
  mobileNo,
  workflowIds,
  provideTasksWithMissingLatAndLong,
  searchFilters,
  columnSearch,
  signal
}) => {
  const token = Cookies.get("accessToken");
  let priorityParam = priority === "Priority" ? "Everything" : priority;
  let statusParam = active === "Status" ? "Everything" : active;

  // Build the params object dynamically
  const params = {
    workflowId,
    workflowIds,
    priority: priorityParam,
    status: statusParam,
    page: currentPage,
    size: pageSize,
    areaCode,
    accountNo,
    assigneeId: selectedUserId,
    fromDate,
    toDate,
    mobileNo,
    provideTasksWithMissingLatAndLong,
    ...searchFilters
  };

  // Conditionally add subActivity only if selectedSubactivity is not empty or undefined
  if (selectedSubactivity) {
    params.subActivity = selectedSubactivity;
  }

  // Columns with no dedicated query param of their own are searched through the
  // generic searchColumn/searchValue pair -- one column at a time, so this is a
  // single pair rather than a repeated one.
  const searchColumn = String(columnSearch?.column ?? "").trim();
  const searchValue = String(columnSearch?.value ?? "").trim();
  if (searchColumn && searchValue) {
    params.searchColumn = searchColumn;
    params.searchValue = searchValue;
  }

  try {
    const response = await axios.get(`${baseURL}api/v1/task/list/get-tasks`, {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal,
    });
    return response.data.body.value;
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

/**
 * Application-level status carried in `header.code`. Every one of these arrives
 * as HTTP 200, so axios never throws for them -- they have to be branched on
 * explicitly by the caller.
 */
export const TASK_LIST_CODES = {
  SUCCESS: 600,
  VALIDATION: 601,
  TOKEN_EXPIRED: 605,
  NO_DATA: 607,
  ACCESS_DENIED: 608,
};

export class TaskListSearchError extends Error {
  constructor(code, message) {
    super(message || `Task list search failed (code ${code})`);
    this.name = "TaskListSearchError";
    this.code = code;
  }
}

/**
 * The filter half of the task-list request body, shared verbatim by
 * POST list/search and POST list/generate-report. The export is defined as
 * "the same query, without paging", so both must be built from this one
 * function -- a second payload builder would drift and silently export a
 * different set of rows than the screen shows.
 */
const buildTaskListFilterBody = ({
  workflowIds,
  assigneeIds,
  provideTasksWithMissingLatAndLong = false,
  columnFilters,
}) => {
  const normalizedWorkflowIds = (Array.isArray(workflowIds) ? workflowIds : [workflowIds])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));

  const body = {
    workflowIds: normalizedWorkflowIds,
    provideTasksWithMissingLatAndLong: Boolean(provideTasksWithMissingLatAndLong),
  };

  // Omit rather than send [] -- an empty array reads as "any assignee" but the
  // backend treats an absent key the same way, and omitting keeps the payload
  // honest about what's actually being constrained.
  const normalizedAssigneeIds = (assigneeIds || [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));
  if (normalizedAssigneeIds.length) {
    body.assigneeIds = normalizedAssigneeIds;
  }

  if (columnFilters?.length) {
    body.columnFilters = columnFilters;
  }

  return body;
};

/**
 * POST api/v1/task/list/search -- the server-side filtered/paged task list.
 *
 * Replaces GET list/get-tasks for this screen: ~40 filterable columns with
 * multi-select values overrun URL length limits, and a JSON body sidesteps
 * having to encode Arabic filter values into a query string. get-tasks is
 * unchanged and still used by the map/other consumers.
 *
 * Filtering runs in SQL across every task in the workflow *before* paging, so
 * `totalSize` is the filtered total -- never recompute it client-side.
 */
export const searchTasks = async ({
  workflowIds,
  page = 0,
  size = 10,
  assigneeIds,
  provideTasksWithMissingLatAndLong = false,
  columnFilters,
  signal,
}) => {
  const token = Cookies.get("accessToken");

  const body = {
    ...buildTaskListFilterBody({
      workflowIds,
      assigneeIds,
      provideTasksWithMissingLatAndLong,
      columnFilters,
    }),
    page,
    size,
  };

  const response = await axios.post(`${baseURL}api/v1/task/list/search`, body, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-App-Type": "WEB",
    },
    signal,
  });

  const code = response.data?.header?.code;
  const value = response.data?.body?.value;

  if (code === TASK_LIST_CODES.SUCCESS) {
    return {
      taskList: value?.taskList || [],
      currentPage: value?.currentPage ?? page,
      totalSize: value?.totalSize ?? 0,
    };
  }

  // "No data" is a legitimate empty result for the current filters, not a
  // failure -- return an empty page so the grid renders its empty state.
  if (code === TASK_LIST_CODES.NO_DATA) {
    return { taskList: [], currentPage: page, totalSize: 0 };
  }

  const message = apiErrorMessage(response.data) || undefined;
  throw new TaskListSearchError(code, message);
};

/**
 * POST api/v1/task/list/generate-report -- queue an Excel export of exactly the
 * rows POST list/search would return for the same filters.
 *
 * Two things this is NOT:
 *  - It is not paged. `page`/`size` are ignored server-side; an export always
 *    covers every matching task, so they are not sent at all.
 *  - It does not return a file. The workbook is built asynchronously and the
 *    response is a plain acknowledgement string, so there is no blob to
 *    download here -- the caller collects the finished file from the reports
 *    list (fetchReportList / the Reports screen).
 *
 * `projectId` is an alternative scope, not an extra filter: passing it exports
 * the whole project and the body's workflowIds are left empty.
 *
 * The sheet's columns are decided entirely by the server from this user type's
 * column-access grants, so no column list is sent.
 *
 * Returns the acknowledgement message on 600; throws TaskListSearchError
 * carrying header.code for everything else (601/605/608), same envelope as the
 * list itself.
 */
export const generateTaskListReport = async ({
  workflowIds,
  projectId,
  assigneeIds,
  provideTasksWithMissingLatAndLong = false,
  columnFilters,
  signal,
}) => {
  const token = Cookies.get("accessToken");

  const body = buildTaskListFilterBody({
    workflowIds,
    assigneeIds,
    provideTasksWithMissingLatAndLong,
    columnFilters,
  });

  const numericProjectId = Number(projectId);
  const hasProjectId = Number.isFinite(numericProjectId) && String(projectId ?? "").trim() !== "";

  const response = await axios.post(`${baseURL}api/v1/task/list/generate-report`, body, {
    ...(hasProjectId ? { params: { projectId: numericProjectId } } : {}),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-App-Type": "WEB",
    },
    signal,
  });

  const code = response.data?.header?.code;
  const value = response.data?.body?.value;
  const message = apiErrorMessage(response.data) || undefined;

  if (code === TASK_LIST_CODES.SUCCESS) {
    return { message };
  }

  // TaskListSearchError's default message says "search", which would read as
  // the wrong operation here -- name the export explicitly when the backend
  // sent no body.value of its own.
  throw new TaskListSearchError(code, message || `Task export failed (code ${code})`);
};

/**
 * POST api/v1/task/list/column-values -- distinct values for one column across
 * every task in the workflow, for the funnel's checkbox list.
 *
 * These have to come from the server: the grid only holds one page, so distinct
 * values derived from it would be a sample of `size` rows out of a workflow
 * that can reach ~400k.
 *
 * `columnConditions` ([{ column, value: string[] }]) narrows the distinct values
 * to rows matching the other columns' selections, Excel-style -- so a funnel
 * only offers values that would still return rows. `limit` caps the number of
 * values returned; both are omitted when unset.
 *
 * Returns [] (rather than throwing) when the endpoint isn't deployed yet or the
 * column has no values, so the picker can fall back to a `contains` box instead
 * of leaving the column unfilterable.
 */
export const fetchColumnValues = async ({
  workflowIds,
  column,
  search,
  limit,
  columnConditions,
  signal,
}) => {
  const token = Cookies.get("accessToken");
  const normalizedWorkflowIds = (Array.isArray(workflowIds) ? workflowIds : [workflowIds])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));

  if (!normalizedWorkflowIds.length || !String(column ?? "").trim()) return [];

  const body = { workflowIds: normalizedWorkflowIds, column };
  if (String(search ?? "").trim()) body.search = String(search).trim();
  const numericLimit = Number(limit);
  if (Number.isFinite(numericLimit) && numericLimit > 0) body.limit = numericLimit;
  const normalizedConditions = (columnConditions || []).filter(
    (condition) =>
      String(condition?.column ?? "").trim() &&
      Array.isArray(condition?.value) &&
      condition.value.length > 0
  );
  if (normalizedConditions.length) body.columnConditions = normalizedConditions;

  try {
    const response = await axios.post(`${baseURL}api/v1/task/list/column-values`, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-App-Type": "WEB",
      },
      signal,
    });

    if (response.data?.header?.code !== TASK_LIST_CODES.SUCCESS) return [];

    const raw = response.data?.body?.value ?? [];
    const list = Array.isArray(raw) ? raw : raw?.values || [];
    // Tolerate ["Smart"], [{value}], [{value,count}] and [{label}] shapes so a
    // late contract tweak on the backend doesn't blank the picker.
    return list
      .map((item) => {
        if (item === null || item === undefined) return "";
        if (typeof item === "object") return String(item.value ?? item.label ?? item.name ?? "");
        return String(item);
      })
      .filter((value, index, all) => all.indexOf(value) === index);
  } catch (error) {
    if (axios.isCancel(error) || error?.name === "CanceledError" || error?.name === "AbortError") {
      throw withApiErrorResponse(error);
    }
    // 404 while the endpoint is still being built is expected, not noteworthy.
    if (error?.response?.status !== 404) {
      console.error(`Failed to load distinct values for "${column}":`, error);
    }
    return [];
  }
};

// Column metadata only changes when the workflow selection changes, so cache it
// per workflowIds set rather than refetching on every mount/filter change.
const taskListColumnsCache = new Map();

export const clearTaskListColumnsCache = () => taskListColumnsCache.clear();

export const fetchTaskListColumns = async (workflowIds, userType) => {
  const cacheKey = JSON.stringify([workflowIds, userType]);
  if (taskListColumnsCache.has(cacheKey)) {
    return taskListColumnsCache.get(cacheKey);
  }

  const token = Cookies.get("accessToken");
  const response = await axios.get(`${baseURL}api/v1/task/list/columns`, {
    params: { workflowIds, userType },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const raw = response.data?.body?.value ?? [];
  const normalizeAccessible = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") return value.toLowerCase() === "true" || value === "1";
    return true;
  };
  const normalizeColumn = (item) => {
    if (typeof item === "string" || typeof item === "number") {
      const key = String(item).trim();
      return key ? { key, label: key, accessible: true } : null;
    }
    if (!item || typeof item !== "object") return null;
    const key = String(
      item.key ??
      item.fieldName ??
      item.columnKey ??
      item.columnName ??
      item.value ??
      item.name ??
      item.label ??
      ""
    ).trim();
    if (!key) return null;
    const label = String(item.label ?? item.displayName ?? key).trim() || key;
    // "standard" = a fixed task column, "details" = a task_details_json key.
    const group = String(item.group ?? "").trim() || "standard";
    return { key, label, group, accessible: normalizeAccessible(item.accessible) };
  };
  const columns = (Array.isArray(raw) ? raw : []).map(normalizeColumn).filter(Boolean);
  taskListColumnsCache.set(cacheKey, columns);
  return columns;
};

export const getTaskDetail = async (taskId) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/task/web/get-task?taskId=${taskId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.body.value;
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

export const updateTaskPriority = async (taskId, newPriority) => {
  try {
    const token = Cookies.get("accessToken");
    const data = {
      taskId: taskId,
      priority: newPriority,
    };

    const response = await axios.post(
      `${baseURL}api/v1/task/update-task-priority`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error updating task priority:", error);
    throw withApiErrorResponse(error);
  }
};

export const deleteTasks = async (selectedtask, comment) => {
  const token = Cookies.get("accessToken");
  const payload = { taskIdList: selectedtask, reason: comment };

  try {
    const response = await axios.post(
      `${baseURL}api/v1/task/multi-delete`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    throw withApiErrorResponse(error);
  }
};

// Function to put tasks on hold
export const putTasksOnHold = async (selectedtask) => {
  const token = Cookies.get("accessToken");
  console.log(selectedtask);
  const payload = { taskIdList: selectedtask };

  try {
    const response = await axios.post(
      `${baseURL}api/v1/task/tasks-on-hold`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    throw withApiErrorResponse(error);
  }
};

// Function to get field agents
export const getFieldAgents = async () => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(`${baseURL}api/v1/user/field-agents`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.body.value;
  } catch (error) {
    throw withApiErrorResponse(error);
  }
};

// Function to delete all records
// Optional filters mirror the list/search + export payload: when any are given,
// only the tasks matching them are deleted. With none, every task in the
// workflow goes (request body unchanged from before).
export const deleteAllRecords = async (
  workflowId,
  comment,
  { assigneeIds, provideTasksWithMissingLatAndLong = false, columnFilters } = {}
) => {
  const token = Cookies.get("accessToken");
  const filterBody = buildTaskListFilterBody({
    workflowIds: workflowId,
    assigneeIds,
    provideTasksWithMissingLatAndLong,
    columnFilters,
  });
  const body = { workflowId, reason: comment };
  if (filterBody.columnFilters) body.columnFilters = filterBody.columnFilters;
  if (filterBody.assigneeIds) body.assigneeIds = filterBody.assigneeIds;
  if (filterBody.provideTasksWithMissingLatAndLong) {
    body.provideTasksWithMissingLatAndLong = true;
  }
  try {
    const response = await axios.post(
      `${baseURL}api/v1/task/delete-tasks`,
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    throw withApiErrorResponse(error);
  }
};

// Function for smart assign
// workflowId is optional -- when given, smart assign runs only for that workflow.
export const smartAssign = async (projectId, isreassignChecked, workflowId) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.post(
      `${baseURL}api/v1/assignment-rule/smart-assign`,
      null, // No request body
      {
        params: { projectId, isReAssign: isreassignChecked, ...(workflowId ? { workflowId } : {}) },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    throw withApiErrorResponse(error);
  }
};

export const fetchCompletedTask = async (taskId) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/task/get/completed-task?taskId=${taskId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    throw withApiErrorResponse(error); // Handle the error as needed
  }
};
export const GetTaskHistory = async (accountNo, page, size) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/task/get-task-history?accountNo=${accountNo}&page=${page}&size=${size}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.body.value;
  } catch (error) {
    throw withApiErrorResponse(error);
  }
};

//task assign
export const fetchFieldAgents = async () => {
  try {
    const token = Cookies.get("accessToken");
    const response = await axios.get(`${baseURL}api/v1/user/field-agents`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.body.value;
  } catch (error) {
    console.error("An error occurred while fetching field agents:", error);
    throw withApiErrorResponse(error); // Propagate the error back to the caller
  }
};

//multi task assign
// export const assignTasks = async (taskIdList, fieldAgentId, workflowId) => {
//   try {
//     const token = Cookies.get("accessToken");
//     const response = await axios.post(
//       `${baseURL}api/v1/task/multi-assign`,
//       {
//         taskIdList,
//         fieldAgentId,
//         workflowId,
//       },
//       {
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${token}`,
//         },
//       }
//     );
//     return withApiError(response.data);
//   } catch (error) {
//     console.error("An error occurred while assigning tasks:", error);
//     throw withApiErrorResponse(error); // Propagate the error back to the caller
//   }
// };

export const fetchPdfData = async (
  workflowId,
  AccountNo,
  currentPage,
  pageSize,
  fromDate,
  toDate,
  radioId = null
) => {
  const token = Cookies.get("accessToken");
  const formatDateTime = (date, isStartOfDay) => {
    const isoString = new Date(date).toISOString();
    return isStartOfDay
      ? isoString.replace("T00:00:00.000Z", "T00:00:00.000")
      : isoString.replace("T00:00:00.000Z", "T23:59:59.999");
  };

  const formattedFromDate = fromDate ? formatDateTime(fromDate, true) : "";
  const formattedToDate = toDate ? formatDateTime(toDate, false) : "";
  const trimmedRadioId =
    radioId != null && String(radioId).trim() !== ""
      ? String(radioId).trim()
      : null;

  const response = await axios.get(`${baseURL}api/v1/task/task-pdf-list`, {
    params: {
      workflowId,
      page: currentPage,
      size: pageSize,
      ...(AccountNo ? { accountNo: AccountNo } : {}),
      ...(formattedFromDate ? { fromDate: formattedFromDate } : {}),
      ...(formattedToDate ? { toDate: formattedToDate } : {}),
      ...(trimmedRadioId ? { radioId: trimmedRadioId } : {}),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data.body.value;
};

export const getWorkflowInputValueList = async (workflowId) => {
  const token = Cookies.get("accessToken");
  if (!workflowId) return [];
  try {
    const response = await axios.get(
      `${baseURL}api/v1/workflow/get-workflow-input-value-list`,
      {
        params: { workflowId },
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    const value = response.data?.body?.value;
    return Array.isArray(value) ? value : [];
  } catch (error) {
    console.error("Error fetching workflow input value list:", error);
    return [];
  }
};

const tryGetFilenameFromContentDisposition = (contentDisposition = "") => {
  // examples:
  // - attachment; filename="task_pdfs.zip"
  // - attachment;filename=task_pdfs.zip
  // - attachment; filename*=UTF-8''task_pdfs.zip
  const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/(^"|"$)/g, ""));

  const match = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
  if (match?.[1]) return match[1];

  return "";
};

export const downloadTaskPdfList = async ({ workflowId, accountNos = [], fromDate, toDate }) => {
  const token = Cookies.get("accessToken");

  if (!workflowId) {
    throw new Error("workflowId is required");
  }

  const accountNosCsv = Array.isArray(accountNos) ? accountNos.filter(Boolean).join(",") : String(accountNos || "");
  const formatDateTime = (date, isStartOfDay) => {
    if (!date) return "";
    const isoString = new Date(date).toISOString();
    return isStartOfDay
      ? isoString.replace("T00:00:00.000Z", "T00:00:00.000")
      : isoString.replace("T00:00:00.000Z", "T23:59:59.999");
  };

  const formattedFromDate = fromDate ? formatDateTime(fromDate, true) : "";
  const formattedToDate = toDate ? formatDateTime(toDate, false) : "";

  const response = await axios.get(`${baseURL}api/v1/task/download-task-pdf-list`, {
    params: {
      workflowId,
      ...(accountNosCsv ? { accountNos: accountNosCsv } : {}),
      ...(formattedFromDate ? { fromDate: formattedFromDate } : {}),
      ...(formattedToDate ? { toDate: formattedToDate } : {}),
    },
    responseType: "blob",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const contentDisposition = response.headers?.["content-disposition"] || "";
  const fileNameFromHeader = tryGetFilenameFromContentDisposition(contentDisposition);
  const fileName = fileNameFromHeader || "task_pdfs.zip";

  const blob = new Blob([response.data], { type: response.headers?.["content-type"] || "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Bulk PDF export (BLF-2171).
 *
 * The three endpoints below always answer HTTP 200 and put the outcome in
 * `header.code` -- 600 success, 601 validation/limit, 607 nothing matched --
 * so callers branch on the code instead of relying on try/catch. The 601 body
 * is already a complete, user-facing sentence naming the limit and the
 * suggested alternative; show it verbatim rather than rewriting it.
 */
export const BULK_PDF_EXPORT_CODE = {
  SUCCESS: 600,
  LIMIT: 601,
  NO_DATA: 607,
};

const BULK_PDF_EXPORT_PATH = "api/v1/task/pdf/bulk-export";

const toBulkPdfExportEnvelope = (data) => ({
  code: data?.header?.code ?? null,
  message: data?.header?.message ?? "",
  value: data?.body?.value ?? null,
});

const normalizeAccountNos = (accountNos) =>
  (Array.isArray(accountNos) ? accountNos : [accountNos])
    .map((accountNo) => String(accountNo ?? "").trim())
    .filter(Boolean);

/**
 * Queue a background bulk export. `accountNos` is optional -- omitting it (or
 * passing an empty list) exports every completed task in the date range.
 * Dates are `yyyy-MM-dd` (bare date = whole day, inclusive). `radioId` is
 * optional and narrows the export to tasks with that radio option.
 * @returns {Promise<{code: number|null, message: string, value: any}>}
 */
export const requestBulkPdfExport = async ({ workflowId, fromDate, toDate, accountNos = [], radioId = null }) => {
  const token = Cookies.get("accessToken");

  if (!workflowId) {
    throw new Error("workflowId is required");
  }

  const selectedAccountNos = normalizeAccountNos(accountNos);
  const trimmedRadioId =
    radioId != null && String(radioId).trim() !== ""
      ? String(radioId).trim()
      : null;

  const response = await axios.post(
    `${baseURL}${BULK_PDF_EXPORT_PATH}/request`,
    {
      workflowId: Number(workflowId),
      ...(fromDate ? { fromDate } : {}),
      ...(toDate ? { toDate } : {}),
      ...(selectedAccountNos.length > 0 ? { accountNos: selectedAccountNos } : {}),
      ...(trimmedRadioId ? { radioId: trimmedRadioId } : {}),
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return toBulkPdfExportEnvelope(response.data);
};

/** Progress snapshot for one export. Status: Pending | InProgress | Completed | Failed. */
export const getBulkPdfExportStatus = async (exportId) => {
  const token = Cookies.get("accessToken");

  if (exportId == null || exportId === "") {
    throw new Error("exportId is required");
  }

  const response = await axios.get(`${baseURL}${BULK_PDF_EXPORT_PATH}/status`, {
    params: { exportId },
    headers: { Authorization: `Bearer ${token}` },
  });

  return toBulkPdfExportEnvelope(response.data);
};

/** Export history, newest first. `workflowId` is optional. */
export const getBulkPdfExportList = async ({ page = 0, size = 10, workflowId = null } = {}) => {
  const token = Cookies.get("accessToken");

  const response = await axios.get(`${baseURL}${BULK_PDF_EXPORT_PATH}/list`, {
    params: {
      page,
      size,
      ...(workflowId ? { workflowId } : {}),
    },
    headers: { Authorization: `Bearer ${token}` },
  });

  return toBulkPdfExportEnvelope(response.data);
};

export const getTaskDetails = async (taskId) => {
  const token = Cookies.get("accessToken");
  return fetch(`${baseURL}api/v1/task/web/get-task?taskId=${taskId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).then((response) => response.json());
};

export const getFieldAgentList = () => {
  const token = Cookies.get("accessToken");
  return axios.get(`${baseURL}api/v1/user/field-agents`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Fetch field agent activity count for Activity Analytics
 * Initially pass projectId, workflowId only; taskStatus and other params are optional filters.
 * Payload shape: { projectId, workflowId [, taskStatus?, fieldAgentId?, fieldAgentName?, fromDate?, toDate? ] }
 * @param {Object} params
 * @param {number} params.projectId - required
 * @param {number|string} params.workflowId - required
 * @param {string} [params.taskStatus] - optional filter (pass empty initially)
 * @param {number|string} [params.fieldAgentId] - optional filter
 * @param {string} [params.fieldAgentName] - optional filter
 * @param {string} [params.fromDate] - optional filter (ISO date or YYYY-MM-DD)
 * @param {string} [params.toDate] - optional filter (ISO date or YYYY-MM-DD)
 * @returns {Promise<Array>} body.value - [{ activity, fieldAgents: [{ fieldAgentId, fieldAgentName, taskCount }] }]
 */
export const getFieldAgentActivityCount = async ({
  projectId,
  workflowId,
  taskStatus,
  fieldAgentId,
  fieldAgentName,
  fromDate,
  toDate,
}) => {
  const token = Cookies.get("accessToken");
  const formatDateForApi = (date, isStartOfDay) => {
    if (!date) return "";
    const d = new Date(date);
    if (isStartOfDay) {
      d.setHours(0, 0, 0, 0);
      // return d.toISOString().slice(0, 19);
    } else {
      d.setHours(23, 59, 59, 999);
    }

    // format in LOCAL time (NOT UTC)
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    // return d.toISOString().slice(0, 19).replace("T23:59:59", "T23:59:59");
  };
  // Required: projectId, workflowId. Optional: taskStatus, fieldAgentId, fieldAgentName, fromDate, toDate (only when filter applied)
  const payload = {
    projectId: Number(projectId),
    workflowId: Number(workflowId),
  };
  if (taskStatus != null && taskStatus !== "") payload.taskStatus = taskStatus;
  if (fromDate) payload.fromDate = formatDateForApi(fromDate, true);
  if (toDate) payload.toDate = formatDateForApi(toDate, false);
  if (fieldAgentId != null && fieldAgentId !== "") payload.fieldAgentId = Number(fieldAgentId);
  if (fieldAgentName != null && fieldAgentName !== "") payload.fieldAgentName = fieldAgentName;
  try {
    const response = await axios.post(
      `${baseURL}api/v1/task/field-agent/activity-count`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data?.header?.code === 600 && response.data?.body?.value) {
      return response.data.body.value;
    }
    return [];
  } catch (error) {
    console.error("Error fetching field agent activity count:", error);
    throw withApiErrorResponse(error);
  }
};

export const getTasks = async (workflowId, taskStatus) => {
  if (!workflowId || !taskStatus) {
    console.warn("Both workflowId and taskStatus are required to fetch tasks");
    return null; // or return an empty array/object depending on your needs
  }

  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/task/map/get-tasks?workflowId=${workflowId}&taskStatus=${taskStatus}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data); // Return the data from the response
  } catch (error) {
    console.error("Error fetching tasks:", error);
    return null; // or handle the error appropriately
  }
};

export const multiassignTasks = async (
  multiAgent,
  selectedfieldAgent,
  workflowId,
  langMode,
  groupId = null,
  byAccountNumbers = false
) => {
  const hasAgents = Array.isArray(selectedfieldAgent) && selectedfieldAgent.length > 0;
  const hasGroup = groupId !== null && groupId !== undefined && groupId !== "";
  // Validate input parameters
  if (!Array.isArray(multiAgent) || multiAgent.length === 0) {
    toast.error(translations[langMode].plzselectatleatone);
    return;
  }
  // re-assign requires a field agent list OR a group (one or both).
  if (!hasAgents && !hasGroup) {
    toast.error(translations[langMode].plzselectedforilfagrntoasing);
    return;
  }
  if (!workflowId) {
    toast.error(translations[langMode].invalidworkflow);
    return;
  }

  const requestBody = JSON.stringify({
    // Bulk re-assign (from bulk upload) sends the entered account numbers; the map/list
    // flows send task ids.
    ...(byAccountNumbers ? { accountNumbers: multiAgent } : { taskIdList: multiAgent }),
    ...(hasAgents ? { fieldAgentIdList: selectedfieldAgent } : {}),
    ...(hasGroup ? { groupId } : {}),
    workflowId: workflowId,
  });

  const token = Cookies.get("accessToken");

  try {
    const response = await fetch(`${baseURL}api/v1/task/re-assign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: requestBody,
    });

    if (!response.ok) {
      // Handle non-200 responses
      const errorMessage = await response.json();
      const backendMessage =
        apiErrorMessage(errorMessage) ||
        errorMessage?.message;
      if (backendMessage) {
        toast.error(backendMessage);
      }
      return;
    }

    const result = await response.json();
    return withApiError(result);
  } catch (error) {
    const backendMessage = getBackendErrorMessage(error);
    if (backendMessage) {
      toast.error(backendMessage);
    }
  }
};

// Bulk re-assign flow: look up which of the entered account numbers already
// exist (with their current assignees) vs. which are new.
export const lookupAccountDetails = async ({ projectId, accountNumbers, workflowId }) => {
  const token = Cookies.get("accessToken");
  const response = await axios.post(
    `${baseURL}api/v1/account-details/lookup`,
    { projectId, accountNumbers, workflowId },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  return withApiError(response.data);
};

export const assignTasks = async (
  multiAgent,
  selectedfieldAgent,
  workflowId,
  langMode,
  groupId = null
) => {
  const hasAgents = Array.isArray(selectedfieldAgent) && selectedfieldAgent.length > 0;
  const hasGroup = groupId !== null && groupId !== undefined && groupId !== "";
  // Validate input parameters
  if (!Array.isArray(multiAgent) || multiAgent.length === 0) {
    toast.error(translations[langMode].plzselectatleatone);
    return;
  }
  // multi-assign requires a field agent list OR a group (one or both).
  if (!hasAgents && !hasGroup) {
    toast.error(translations[langMode].plzselectedforilfagrntoasing);
    return;
  }
  if (!workflowId) {
    toast.error(translations[langMode].invalidworkflow);
    return;
  }

  const requestBody = JSON.stringify({
    taskIdList: multiAgent,
    ...(hasAgents ? { fieldAgentIdList: selectedfieldAgent } : {}),
    ...(hasGroup ? { groupId } : {}),
    workflowId: workflowId,
  });

  const token = Cookies.get("accessToken");

  try {
    const response = await fetch(`${baseURL}api/v1/task/multi-assign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: requestBody,
    });

    if (!response.ok) {
      // Handle non-200 responses
      const errorMessage = await response.json();
      const backendMessage =
        apiErrorMessage(errorMessage) ||
        errorMessage?.message;
      if (backendMessage) {
        toast.error(backendMessage);
      }
      return;
    }

    const result = await response.json();
    return withApiError(result);
  } catch (error) {
    const backendMessage = getBackendErrorMessage(error);
    if (backendMessage) {
      toast.error(backendMessage);
    }
  }
};

export const deleteTask = async (taskId) => {
  const temp = JSON.stringify({ taskIdList: [taskId] });
  const token = Cookies.get("accessToken");
  return fetch(`${baseURL}api/v1/task/multi-delete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: temp,
  }).then((response) => response.json());
};

//taskdetail page

export const fetchTaskDetail = async (taskId) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/task/web/get-task-with-metadata`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          taskId,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    throw new Error("Error fetching task details");
  }
};

export const updateTaskDetails = async (updatedTask) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.post(
      `${baseURL}api/v1/task/update-task`,
      updatedTask,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    throw new Error("Error updating task details");
  }
};

//export-report-and-data data

export const fetchExportData = (
  selectedOption,
  startDate,
  endDate,
  isIncludeDeletedRecords = true,
  radioId = null,
  cycle = null
) => {
  const token = Cookies.get("accessToken");

  // The backend accepts exactly one scope -- a date range OR a cycle. Sending
  // both (or neither) is a validation error, so the unused one is left off the
  // query entirely rather than sent empty.
  const formattedStartDate = startDate
    ? moment(startDate).format("YYYY-MM-DDTHH:mm:ss")
    : null;
  const formattedEndDate = endDate
    ? moment(endDate).format("YYYY-MM-DDTHH:mm:ss")
    : null;

  // Convert selectedOption array to a comma-separated string
  const workflowIds = selectedOption.join(",");

  const trimmedRadioId =
    radioId != null && String(radioId).trim() !== ""
      ? String(radioId).trim()
      : null;

  const trimmedCycle =
    cycle != null && String(cycle).trim() !== "" ? String(cycle).trim() : null;

  return axios.get(`${baseURL}api/v1/task/generate-report`, {
    params: {
      workflowIds: workflowIds, // Pass the workflow IDs as a comma-separated string
      ...(formattedStartDate ? { startDate: formattedStartDate } : {}),
      ...(formattedEndDate ? { endDate: formattedEndDate } : {}),
      includeDeletedRecords: isIncludeDeletedRecords,
      ...(trimmedRadioId ? { radioId: trimmedRadioId } : {}),
      ...(trimmedCycle ? { cycle: trimmedCycle } : {}),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

// Cycles a workflow already has uploads for, as stored (e.g. "march-2026").
// Values are echoed straight back on generate-report / cycle-performance-report,
// so they are never reformatted here. Empty list = no cycle uploads yet.
// Resolves to [] on failure so the dropdown just renders empty instead of
// breaking the screen.
export const getCycleListByWorkflowId = async (workflowId) => {
  if (!workflowId) {
    return [];
  }

  const token = Cookies.get("accessToken");

  // The endpoint takes `workflowIds` as a list -- sending the singular
  // `workflowId` is rejected with "Parameter: workflowIds(List) is a mandatory
  // field". It is still queried one workflow at a time on purpose: the caller
  // needs to know which cycles belong to which workflow, and a combined
  // response would only give back the union. Comma-separated to match how
  // generate-report already passes workflowIds.
  try {
    const response = await axios.get(`${baseURL}api/v1/task/cycles`, {
      params: { workflowIds: String(workflowId) },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data?.header?.code === 600) {
      const value = response.data.body?.value;
      return Array.isArray(value) ? value : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching cycle list:", error);
    return [];
  }
};

// Cycle performance report for one or more { workflowId, cycle } pairs. Runs
// inline on the backend -- unlike the queued generate-report flow, there is
// nothing to poll for. The results show up in report-request/get-list as
// `performanceCycleReport`.
export const generateCyclePerformanceReport = async (activities) => {
  const token = Cookies.get("accessToken");

  // Body key is `activities` -- { activities: [{ workflowId, cycle }] }.
  const response = await axios.post(
    `${baseURL}api/v1/task/cycle-performance-report`,
    { activities },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  return withApiError(response.data);
};

export const fetchReportList = async (currentPage, pageSize) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/report-request/get-list?page=${currentPage}&size=${pageSize}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data.header.code === 600) {
      const { reportRequestList, totalSize } = response.data.body.value;
      return { reportRequestList, totalSize };
    } else {
      console.error("Unexpected response code:", response.data.header.code);
      return { reportRequestList: [], totalSize: 0 };
    }
  } catch (error) {
    console.error("Error fetching report list:", error);
    return { reportRequestList: [], totalSize: 0 };
  }
};
//create task page

export const fetchTaskForm = async (workflowId) => {
  if (!workflowId) {
    console.warn("workflowId is required to fetch Data");
    return {};
  }
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/task-meta/get-task-form?workflowId=${workflowId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching data for activity:", error);
    throw withApiErrorResponse(error);
  }
};

export const saveTask = async (workflowId, taskData, accountNo, selectedfieldAgent, allowUpdateIfExists, subcategoryIds, selectedStatus) => {
  const token = Cookies.get("accessToken");

  // Filter out numeric keys
  const filteredTaskDetails = Object.keys(taskData)
    .filter((key) => isNaN(key)) // Keep only non-numeric keys
    .reduce((obj, key) => {
      obj[key] = taskData[key];
      return obj;
    }, {});

  const requestData = {
    workflowId,
    priority: selectedStatus || "Low_Priority",
    taskDetailsJson: filteredTaskDetails,
    accountNo,
    assigneeIds: selectedfieldAgent,
    allowUpdateIfExists,
    taskCategory: subcategoryIds,
    taskPriority: [selectedStatus]
  };

  try {
    const response = await axios.post(
      `${baseURL}api/v1/task/update-task`,
      requestData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error saving task:", error);
    throw withApiErrorResponse(error);
  }
};

/*
  Code Developer Shivani Vaghani
 */
export const saveBulkAssignTask = async (accountNumbers, departmentId, fieldAgentIds, workflowId, organizationId) => {
  const token = Cookies.get("accessToken");

  const requestData = {
    accountNumbers,
    departmentId,
    fieldAgentIds,
    workflowId,
    organizationId
  };

  try {
    const response = await axios.post(
      `${baseURL}api/v1/task/create-assign-bulk-task`,
      requestData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        responseType: "blob",
      }
    );

    const contentType = response.headers["content-type"];

    if (contentType.includes("application/json")) {
      // ? Convert Blob to JSON (if API returned JSON)
      const text = await response.data.text();
      return JSON.parse(text);
    }

    const blob = new Blob([response.data], { type: response.headers["content-type"] });

    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);

    const contentDisposition = response.headers["content-disposition"];
    let fileName = "errorAccountList.xlsx";

    if (contentDisposition) {
      const matches = contentDisposition(/filename="?([^"]+)"?/);
      if (matches?.length > 1) {
        fileName = matches[1];
      }
    }

    link.download = fileName;
    document.body.appendChild(link);

    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
    return null;
  } catch (error) {
    console.error("Error in saveBulkAssignTask:", error);
    const backendMessage = getBackendErrorMessage(error);
    if (backendMessage) {
      toast.error(backendMessage);
    }
  }
};

/*
  Code Developer Shivani Vaghani
 */
export const saveBulkTask = async (data) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.post(
      `${baseURL}api/v1/task/update-bulk-task`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        responseType: "blob",
      }
    );

    const contentType = response.headers["content-type"];

    if (contentType.includes("application/json")) {
      // ? Convert Blob to JSON (if API returned JSON)
      const text = await response.data.text();
      return JSON.parse(text);
    }

    const blob = new Blob([response.data], { type: response.headers["content-type"] });

    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);

    const contentDisposition = response.headers["content-disposition"];
    let fileName = "errorAccountList.xlsx";

    if (contentDisposition) {
      const matches = contentDisposition(/filename="?([^"]+)"?/);
      if (matches?.length > 1) {
        fileName = matches[1];
      }
    }

    link.download = fileName;
    document.body.appendChild(link);

    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
    return null;
  } catch (error) {
    console.error("Error in saveBulkAssignTask:", error);
    const backendMessage = getBackendErrorMessage(error);
    if (backendMessage) {
      toast.error(backendMessage);
    }
  }
};

export const fetchFilterExcel = async ({
  workflowIds,
  priority,
  active,
  areaCode,
  accountNo,
  assigneeId,
  organizationId,
  projectId,
  includeProjectId,
  fromDate,
  toDate
}) => {
  const token = Cookies.get("accessToken");
  let priorityParam = priority === "Priority" ? "Everything" : priority;
  let statusParam = active === "Status" ? "Everything" : active;
  try {
    const response = await axios.get(
      `${baseURL}api/v1/task/list/generate-report`,
      {
        params: {
          workflowIds,
          priority: priorityParam,
          status: statusParam,
          areaCode,
          accountNo,
          assigneeId,
          ...(includeProjectId && { projectId }),
          fromDate,
          toDate
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("An error occurred:", error);
    throw withApiErrorResponse(error);
  }
};

export const fetchFieldAgentMap = async (workflowId) => {
  const token = Cookies.get("accessToken");
  // const userType = Cookies.get("userType");

  // resolved params issue by harshit vachhani
  if (!workflowId) {
    return;
  }

  const url =
    `${baseURL}api/v1/user/field-agents-by-workflow?workflowId=${workflowId}`
  // userType === "super_admin" && workflowId
  //? `${baseURL}api/v1/user/field-agents?organizationId=${selectedOrgId}`

  //: `${baseURL}api/v1/user/field-agents`;

  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return withApiError(response.data);
};

export const fetchFieldAgentMapProject = async (projectId) => {
  const token = Cookies.get("accessToken");
  const url = `${baseURL}api/v1/user/web/field-agents-by-project?projectId=${projectId}`;
  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return withApiError(response.data); // This is the array you want
};

// get agents by workflow id 
export const fetchAgentByWorkflow = async (workflowId) => {
  const token = Cookies.get("accessToken");


  if (!workflowId) {
    return;
  }

  const url = `${baseURL}api/v1/user/field-agents-by-workflow?workflowId=${workflowId}`

  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data?.body?.value;
};

export const Subactivity = (workflowId) => {
  const token = Cookies.get("accessToken");
  return axios.get(
    `${baseURL}api/v1/workflow/sub-activity-list?workflowId=${workflowId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

export const fetchMetadataColumn = async (workflowId) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/task-meta/excel-upload/get-field-names?workflowId=${workflowId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching organization data:", error);
    throw withApiErrorResponse(error);
  }
};

export const fetchExcelDetails = async (workflowId, currentPage, pageSize) => {
  if (!workflowId) {
    console.warn("workflowId is required to fetch Data");
    return {};
  }
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/task-excel/get-all?workflowId=${workflowId}&page=${currentPage}&size=${pageSize}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching data for activity:", error);
    throw withApiErrorResponse(error);
  }
};

export const taskHistoryDetails = async (
  accountNumber,
  organizationId = ""
) => {
  if (!accountNumber) {
    console.warn("accountNumber is required to fetch Data");
    return {};
  }

  const token = Cookies.get("accessToken");

  try {
    // Append organizationId to the API URL only if it's provided
    const response = await axios.get(
      `${baseURL}api/v1/account-details/history/get-all?accountNo=${accountNumber}&organizationId=${organizationId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching data for task history:", error);
    throw withApiErrorResponse(error);
  }
};

export const taskHistoryDetailsByOrg = async (
  accountNumber,
  organizationId
) => {
  if (!accountNumber) {
    console.warn("accountNumber is required to fetch Data");
    return {};
  }

  if (!organizationId) {
    console.warn("organizationId is required to fetch Data");
    return {};
  }

  const token = Cookies.get("accessToken");

  try {
    // Append organizationId to the API URL only if it's provided
    const response = await axios.get(
      `${baseURL}api/v1/account-details/history/get-all-by-org?accountNo=${accountNumber}&organizationId=${organizationId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching data for task history:", error);
    throw withApiErrorResponse(error);
  }
};


export const fetchFieldsValue = async (activityId, accountNo, selectedOrgId, projectId) => {
  if (!accountNo) {
    console.warn("accountNo is required to fetch Data");
    return {};
  }

  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/account-details/get?organizationId=${selectedOrgId}&accountNo=${accountNo}&projectId=${projectId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching data for activity:", error);
    throw withApiErrorResponse(error);
  }
};

export const fetchFieldsValues = async (selectedOrgId, accountNo, projectId) => {
  if (!accountNo) {
    console.warn("accountNo is required to fetch Data");
    return {};
  }
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/account-details/get?organizationId=${selectedOrgId}&accountNo=${accountNo}&projectId=${projectId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching data for activity:", error);
    throw withApiErrorResponse(error);
  }
};

export const masterRecordApi = async (projectId, fileData) => {
  const token = Cookies.get("accessToken");

  let formData = new FormData();
  formData.append("excel", fileData);
  formData.append("projectId", projectId);
  return fetch(`${baseURL}api/v1/account-details/excel-upload`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  }).then((response) => response.json());
};

export const updateCompletedTask = async (taskData) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.post(
      `${baseURL}api/v1/task/update/completed-task`,
      taskData, // Ensure taskData is passed as the request body
      {
        headers: {
          Authorization: `Bearer ${token}`, // Include the token in the headers
        },
      }
    );

    return withApiError(response.data); // Return the complete response data
  } catch (error) {
    console.error("Error updating completed task:", error);
    throw withApiErrorResponse(error); // Throw the error to be handled in the handleSubmit function
  }
};

export const unassignTask = async ({ workflowId, taskIds, assigneeId = null, groupId = null }) => {
  const token = Cookies.get("accessToken");
  try {
    const payload = {
      workflowId,
      taskIds, // Required field
      ...(assigneeId && { assigneeId }), // Include assigneeId if present
      ...(groupId !== null && groupId !== undefined && groupId !== "" && { groupId }), // Optional group unassign
    };

    const response = await axios.post(
      `${baseURL}api/v1/task/un-assign`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`, // Include the token in the headers
        },
      }
    );

    return withApiError(response.data);
  } catch (error) {
    console.error("Error unassigning task:", error);
    throw withApiErrorResponse(error); // Rethrow to handle it in the calling function
  }
};

// export const unassignTask = async (task) => {
//   try {
//     const response = await axios.post(`${baseURL}api/v1/task/un-assign`, task, {
//       headers: {
//         Authorization: `Bearer ${token}`, // Include the token in the headers
//       },
//     });
//     return withApiError(response.data);
//   } catch (error) {
//     console.error("Error unassigning task:", error);
//     throw withApiErrorResponse(error); // Rethrow to handle it in the calling function
//   }
// };

export const softdeletefunction = async (fileid,langMode) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await fetch(
      `${baseURL}api/v1/task-excel/soft-delete-all?id=${fileid}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.ok) {
      const responseData = await response.json();
      if (responseData.header.code === 600) {
        toast.success(translations[langMode].softdeletedsucc); // Display success toast
      } else {
        toast.error(apiErrorMessage(responseData, translations[langMode].softdelfailed));
      }
    } else {
        toast.error(translations[langMode].softdelfailed); // Display error toast
    }
  } catch (error) {
    console.error("Error during soft delete:", error);
    toast.error(translations[langMode].erroduringsoftdel); // Display error toast in case of error
  }
};

export const permanantdeletefunction = async (fileid,langMode) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await fetch(
      `${baseURL}api/v1/task-excel/delete-all?id=${fileid}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.ok) {
      const responseData = await response.json();
      if (responseData.header.code === 600) {
        toast.success(translations[langMode].permamatdelsuc); // Display success toast
      } else {
        toast.error(apiErrorMessage(responseData, translations[langMode].permantdelfailed));
      }
    } else {
        toast.error(translations[langMode].permantdelfailed); // Display error toast
    }
  } catch (error) {
    console.error("Error during permanent delete:", error);
    toast.error(translations[langMode].errorduringperdel); // Display error toast in case of error
  }
};

export const recallfunction = async (fileid,langMode) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await fetch(
      `${baseURL}api/v1/task-excel/re-call?id=${fileid}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.ok) {
      const responseData = await response.json();

      // Check the header.code for 600
      if (responseData.header.code === 600) {
        toast.success(translations[langMode].taskrecallcompletesuc); // Display success toast
      } else {
        toast.error(apiErrorMessage(responseData, translations[langMode].taskrecallfailed));
      }
    } else {
        toast.error(translations[langMode].taskrecallfailed); // Display error toast for other codes
    }
  } catch (error) {
    console.error("Error during task recall:", error);
    toast.error(translations[langMode].erroduringtaskrecall); // Display error toast in case of an exception
  }
};


export const fetchMetaData = async (workflowId) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/workflow/get-workflow-metadata`,
      {
        params: { workflowId },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    const data = response.data.body.value;
    return data
  } catch (error) {
    throw new Error("Error fetching workflow data: " + error.message);
  }
};

export const taskHistoryDetailsByDept = async (
  accountNumber,
  departmentId
) => {
  if (!accountNumber) {
    console.warn("accountNumber is required to fetch Data");
    return {};
  }
  if (!departmentId) {
    console.warn("departmentId is required to fetch Data");
    return {};
  }

  const token = Cookies.get("accessToken");

  try {
    // Append organizationId to the API URL only if it's provided
    const response = await axios.get(
      `${baseURL}api/v1/account-details/history/get-all-by-dept?accountNo=${accountNumber}&departmentId=${departmentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching data for task history:", error);
    throw withApiErrorResponse(error);
  }
};


export const fetchAccountHistory = async (
  accountNumber,
  projectId,
  organizationId
) => {
  if (!accountNumber) {
    console.warn("accountNumber is required to fetch Data");
    return {};
  }

  const token = Cookies.get("accessToken");

  try {
    // projectId is optional: without it the backend returns the account's history across every
    // department/project the logged-in user is permitted to see. A super admin (not limited to
    // assigned projects) also sends the organization whose departments/projects should be searched.
    const params = { accountNo: accountNumber };
    if (projectId) params.projectId = projectId;
    if (organizationId) params.organizationId = organizationId;
    const response = await axios.get(
      `${baseURL}api/v1/account-details/history/get-all`,
      {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching data for task history:", error);
    throw withApiErrorResponse(error);
  }
};

// Full lifecycle roadmap (created -> started -> assigned -> completed ...) for a
// single task, used by the Task Roadmap drawer on the Account History screen.
export const fetchTaskRoadmap = async (taskId, { signal } = {}) => {
  if (!taskId) {
    console.warn("taskId is required to fetch the task roadmap");
    return {};
  }

  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/account-details/history/roadmap`,
      {
        params: { taskId },
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        signal,
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error fetching task roadmap:", error);
    throw withApiErrorResponse(error);
  }
};

export const saveBulkAssignTaskByDept = async (data) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.post(
      `${baseURL}api/v1/task/create-assign-bulk-task`,
      data,

      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        responseType: "blob",
      }
    );

    const contentType = response.headers["content-type"];

    if (contentType.includes("application/json")) {
      // ? Convert Blob to JSON (if API returned JSON)
      const text = await response.data.text();
      return JSON.parse(text);
    }

    const blob = new Blob([response.data], { type: response.headers["content-type"] });

    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);

    const contentDisposition = response.headers["content-disposition"];
    let fileName = "errorAccountList.xlsx";

    if (contentDisposition) {
      const matches = contentDisposition(/filename="?([^"]+)"?/);
      if (matches?.length > 1) {
        fileName = matches[1];
      }
    }

    link.download = fileName;
    document.body.appendChild(link);

    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
    return null;
  } catch (error) {
    console.error("Error in saveBulkAssignTask:", error);
    const backendMessage = getBackendErrorMessage(error);
    if (backendMessage) {
      toast.error(backendMessage);
    }
  }
};


export const fetchPdfDataTaskList = async (data) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await axios.get(
      `${baseURL}api/v1/task/get/pdf-link?taskId=${data}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data.body.value;
  } catch (error) {
    console.error("Error fetching PDF link:", error);
    throw withApiErrorResponse(error);
  }
}

/**
 * On-request task PDF. Account History / the Task Roadmap drawer only carry a
 * `pdfLink` for tasks whose report was already generated; for the rest this
 * endpoint generates it on the spot. The response shape is identical either
 * way (code 600, S3 URL in `body.value`), so callers don't need to care which
 * happened.
 *
 * Validation failures come back as code 601 with the message in `body.value`
 * and an HTTP 200 by design, so they're surfaced as `errorMessage` rather than
 * thrown -- only transport/auth failures reject.
 */
export const generateTaskPdfLink = async ({ taskId, accountNo }) => {
  if (!taskId) {
    console.warn("taskId is required to generate the task PDF");
    return { pdfLink: "", errorMessage: "" };
  }

  const token = Cookies.get("accessToken");

  const response = await axios.get(`${baseURL}api/v1/task/generate-task-pdf`, {
    params: { taskId, accountNo },
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const { header, body } = response?.data || {};
  const value = typeof body?.value === "string" ? body.value.trim() : "";

  if (header?.code === 600 && value) {
    return { pdfLink: value, errorMessage: "" };
  }

  return {
    pdfLink: "",
    errorMessage: (typeof body?.error === "string" && body.error.trim()) || value || header?.message || "",
  };
};


export const getFilteredTasks2 = async (activityId = [], selectData, selectedOrgId) => {
  const token = Cookies.get("accessToken");

  const requestData = {
    workflowIds: [activityId],
    ...selectData,
    organizationId: selectedOrgId,
  }

  try {
    const response = await axios.post(
      `${baseURL}api/v1/task/map/get-filtered-tasks2`,
      requestData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return withApiError(response.data);
  } catch (error) {
    console.error("Error saving task:", error);
    throw withApiErrorResponse(error);
  }
};

export const fetchSubcategories = async (activityId) => {
  const token = Cookies.get("accessToken");

  try {
    const response = await fetch(`${baseURL}api/v1/sub-categories?workflowIds=${activityId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json(); // Attempt to parse error response
      throw new Error(apiErrorMessage(errorData) || `HTTP error! Status: ${response.status}`);
    }
    const responseData = await response.json();
    return withApiError(responseData) || []; // Ensure it returns an array, even if data is null/undefined
  } catch (error) {
    console.error("Error fetching sub-categories:", error);
    // You can re-throw the error if you want the component to handle it explicitly
    throw withApiErrorResponse(error);
  }
};

export const createSubCategory = async (name, arabicName, activityId) => {
  const token = Cookies.get("accessToken"); // Assuming Cookies is defined and available
  try {
    const response = await fetch(`${baseURL}api/v1/sub-categories/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ categories: name, arabicName, workflowId: activityId }),
    });
    if (!response.ok) {
      const errorData = await response.json(); // Attempt to parse error response
      throw new Error(apiErrorMessage(errorData) || `HTTP error! Status: ${response.status}`);
    }
    const responseData = await response.json();
    return withApiError(responseData); // This will now return the full { header: { code: 600 }, body: { ... } } object
  } catch (error) {
    console.error("Error creating sub-category:", error);
    throw withApiErrorResponse(error); // Re-throw the error so `handleAddSubCategory` can catch it
  }
};

export const updateSubCategory = async (id, name, arabicName) => {
  const token = Cookies.get("accessToken"); // Assuming Cookies is defined and available

  try {
    const response = await fetch(`${baseURL}api/v1/sub-categories/update/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ name, arabicName }),
    });
    if (!response.ok) {
      const errorData = await response.json(); // Attempt to parse error response
      throw new Error(apiErrorMessage(errorData) || `HTTP error! Status: ${response.status}`);
    }
    const responseData = await response.json();
    return withApiError(responseData);
  } catch (error) {
    console.error("Error creating sub-category:", error);
    throw withApiErrorResponse(error); // Re-throw the error so `handleAddSubCategory` can catch it
  }
};
// --- Priority API Functions ---

export const fetchPriorities = async () => {
  const token = Cookies.get("accessToken");

  try {
    const response = await fetch(`${baseURL}api/v1/get-priority`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json(); // Attempt to parse error response
      throw new Error(apiErrorMessage(errorData) || `HTTP error! Status: ${response.status}`);
    }
    const responseData = await response.json();
    return withApiError(responseData);
  } catch (error) {
    console.error("Error fetching priorities:", error);
    throw withApiErrorResponse(error);
  }
};

export const createPriority = async (name) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await fetch(`${baseURL}api/v1/priority/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ status: name }),
    });
    if (!response.ok) {
      const errorData = await response.json(); // Attempt to parse error response
      throw new Error(apiErrorMessage(errorData) || `HTTP error! Status: ${response.status}`);
    }
    const responseData = await response.json();
    return withApiError(responseData); // This will now return the full { header: { code: 600 }, body: { ... } } object
  } catch (error) {
    console.error("Error creating priority:", error);
    throw withApiErrorResponse(error);
  }
};


export const getFieldNames = async () => {
  const token = Cookies.get("accessToken");

  try {
    const res = await axios.get(`${baseURL}api/v1/account-details/get-field-names`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data;
  } catch (err) {
    throw normalizeApiErrorData(err.response?.data) || err;
  }
};

// Upload excel with mapping
export const uploadExcel = async (file, projectId, selectedOrgId, mapping) => {
  const token = Cookies.get("accessToken");

  try {
    const formData = new FormData();
    formData.append("excel", file);
    formData.append("projectId", projectId);
    formData.append("organizationId", selectedOrgId);
    formData.append("columnMappings", JSON.stringify(mapping));

    const res = await axios.post(`${baseURL}api/v1/account-details/excel-upload`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });

    return res.data;
  } catch (err) {
    throw normalizeApiErrorData(err.response?.data) || err;
  }
};


export const getDcrcWorkflowNaming = async (workflowId) => {
  if (!workflowId) return { subActivityList: [] };
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(
      `${baseURL}api/v1/workflow/dcrc-workflow-naming`,
      {
        params: { workflowId },
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    const value = response.data?.body?.value;
    return {
      subActivityList: Array.isArray(value?.subActivityList) ? value.subActivityList : [],
      workflowId: value?.workflowId,
      workflowName: value?.workflowName,
    };
  } catch (error) {
    console.error("Error fetching DCRC workflow naming:", error);
    return { subActivityList: [] };
  }
};
